import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicSignRequest, publicSignStatement } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Signature, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/t";

/**
 * Bishop-facing signature page. Reached via a signed URL emailed by staff.
 * No account or login required — the HMAC token in the URL is the bearer
 * of authority to sign as this specific bishop.
 *
 * Copy is localized via ``useT`` so a bishop or their bilingual chancery
 * assistant can toggle the language via the site language switcher.
 */
export default function BishopSignRequest() {
  const t = useT();
  const { sid } = useParams();
  const [params] = useSearchParams();
  const bishop_id = params.get("bishop_id") || "";
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [autoApproved, setAutoApproved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await publicSignRequest(sid, bishop_id, token);
        setData(r);
      } catch (e) {
        setError(e?.response?.data?.detail || t("priest_signature.generic_fail"));
      } finally { setLoading(false); }
    })();
  }, [sid, bishop_id, token]);

  const sign = async () => {
    setSigning(true);
    try {
      const r = await publicSignStatement(sid, bishop_id, token);
      setSigned(true);
      setAutoApproved(!!r.auto_approved);
      toast.success(t("priest_signature.thanks"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("priest_signature.generic_fail"));
    } finally { setSigning(false); }
  };

  return (
    <div className="min-h-screen bg-ccc-paper" data-testid="bishop-sign-page">
      <header className="bg-ccc-plum text-ccc-paper py-6">
        <div className="max-w-3xl mx-auto px-6">
          <div className="eyebrow eyebrow-light">Colorado Catholic Conference</div>
          <div className="font-serif text-2xl">{t("priest_signature.title")}</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        {loading && <div className="text-ccc-muted" style={{ fontSize: "15px" }}>{t("common.loading")}</div>}

        {error && (
          <div className="bg-white border border-ccc-rule border-l-4 border-l-red-500 p-8 text-center" data-testid="bishop-sign-error">
            <XCircle className="mx-auto text-red-600" size={40}/>
            <div className="font-serif text-2xl text-ccc-plum mt-3">{t("common.not_found")}</div>
            <p className="text-ccc-body mt-2" style={{ fontSize: "16px" }}>{error}</p>
          </div>
        )}

        {data && (
          <>
            <div className="bg-white border border-ccc-rule p-6 mb-6" data-testid="bishop-sign-bishop">
              <div className="eyebrow" style={{ fontSize: "12px" }}>{t("priest_signature.signing_as_label")}</div>
              <div className="font-serif text-2xl text-ccc-plum">{data.bishop.name}</div>
              <div className="text-ccc-body" style={{ fontSize: "15px" }}>{data.bishop.diocese}</div>
            </div>

            <div className="bg-white border border-ccc-rule p-6 mb-6" data-testid="bishop-sign-statement">
              <div className="eyebrow" style={{ fontSize: "12px" }}>
                {data.statement.type} · {(data.statement.submitted_at || "").slice(0, 10)}
              </div>
              <h1 className="font-serif text-3xl text-ccc-plum mt-2 mb-4">{data.statement.title}</h1>
              {data.statement.approval_message && (
                <div className="border-l-4 border-ccc-plum pl-3 py-2 mb-4 bg-ccc-paper text-ccc-body italic" style={{ fontSize: "15px" }}>
                  {data.statement.approval_message}
                </div>
              )}
              <div className="text-ccc-ink mb-4" style={{ fontSize: "15px" }}>
                <strong>{t("priest_signature.author_label")}:</strong> {data.statement.author}
              </div>
              <div className="border-t border-ccc-rule pt-4 font-serif whitespace-pre-line text-ccc-ink leading-relaxed">
                {data.statement.body}
              </div>
            </div>

            {(signed || data.already_signed || data.already_completed) ? (
              <div className="bg-white border border-ccc-rule border-l-4 border-l-ccc-gold p-6 text-center" data-testid="bishop-sign-thanks">
                <CheckCircle2 className="mx-auto text-ccc-plum" size={40}/>
                <div className="font-serif text-2xl text-ccc-plum mt-2">
                  {t("priest_signature.thanks")}
                </div>
                {data.already_completed && !data.already_signed && (
                  <p className="text-ccc-body mt-2" style={{ fontSize: "15px" }}>
                    {t("priest_signature.already_completed")}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-white border border-ccc-rule p-6">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className="text-ccc-plum flex-shrink-0" size={20}/>
                  <div className="text-ccc-body" style={{ fontSize: "15px" }}>
                    {t("priest_signature.signing_as_label")}: <strong>{data.bishop.name}</strong>.
                  </div>
                </div>
                <Button onClick={sign} disabled={signing}
                        className="bg-ccc-plum hover:bg-ccc-plum-deep text-ccc-paper w-full py-6 uppercase tracking-[0.08em] font-semibold"
                        data-testid="bishop-sign-btn"
                        style={{ fontSize: "15px" }}>
                  <Signature size={16} className="mr-2"/>
                  {signing ? t("priest_signature.submitting") : t("priest_signature.submit")}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
